/**
 * Health API client (tech-spec §5.3, §10.1).
 *
 * Backend already serves /api/health (shallow) and /api/health/deep
 * (per-check, server-cached 30s). We never touch /actuator endpoints
 * directly — only the typed JSON ones.
 */

export type HealthStatus = 'ok' | 'degraded' | 'down'

export type ShallowHealth = {
  status: HealthStatus
  version: string
  uptimeSeconds: number
}

export type DeepHealth = {
  status: HealthStatus
  version: string
  uptimeSeconds: number
  checks: Record<string, { status: HealthStatus; latencyMs: number }>
}

export async function fetchShallowHealth(signal?: AbortSignal): Promise<ShallowHealth | { status: 'down' }> {
  try {
    const response = await fetch('/api/health', { signal })
    if (!response.ok) return { status: 'down' }
    return (await response.json()) as ShallowHealth
  } catch {
    return { status: 'down' }
  }
}

export async function fetchDeepHealth(signal?: AbortSignal): Promise<DeepHealth | { status: 'down' }> {
  try {
    const response = await fetch('/api/health/deep', { signal })
    if (!response.ok) return { status: 'down' }
    return (await response.json()) as DeepHealth
  } catch {
    return { status: 'down' }
  }
}
